import classNames from "classnames";
import React, { HTMLAttributes, ReactNode } from "react";
import { Svg, SvgSize } from "../../Assets/Svg/React";

export enum SimpleInfoAlertType {
  Default = 0,
  Info = 1,
  Warning = 2,
  Critical = 3
}

export interface SimpleInfoAlertProps {
  /** Text content for the alert. Can also use children. */
  text?: string | ReactNode;
  /** Alert severity/type */
  type?: SimpleInfoAlertType;
  /** Additional CSS class names */
  className?: string;
  /** Extra HTML attributes to spread on the wrapper div */
  extraAttributes?: HTMLAttributes<HTMLDivElement>;
  /** Optional children to render instead of `text` */
  children?: ReactNode;
  /** Optional system icon symbol name (defaults to "about") */
  symbol?: string;
  /**
   * Smaller padding, text and icon (24px tall in all), rendered as an inline `<span>` so
   * the alert can sit inside a link or label (e.g. a tag beside a name in a list row)
   */
  compact?: boolean;
}

/**
 * SimpleInfoAlert - React equivalent of the server-rendered SimpleInfoAlert.cshtml
 */
export function SimpleInfoAlert({
  text,
  type = SimpleInfoAlertType.Default,
  className = "",
  extraAttributes,
  children,
  symbol = "about",
  compact = false
}: SimpleInfoAlertProps) {
  const content = children ?? text;

  if (!content || (typeof content === "string" && content.toString().trim() === "")) {
    return null;
  }

  const padding =
    type === SimpleInfoAlertType.Default ? "p-0" : compact ? "px-xs py-px" : "p-md";
  const Tag = compact ? "span" : "div";

  const textColor =
    type === SimpleInfoAlertType.Info
      ? "text-t-light-blue"
      : type === SimpleInfoAlertType.Warning
        ? "text-t-yellow"
        : type === SimpleInfoAlertType.Critical
          ? "text-t-pink"
          : "text-t-neutral-800";

  const backgroundColor =
    type === SimpleInfoAlertType.Info
      ? "bg-light-blue-400"
      : type === SimpleInfoAlertType.Warning
        ? "bg-yellow-400"
        : type === SimpleInfoAlertType.Critical
          ? "bg-pink-400"
          : "bg-transparent";

  const borderColor =
    type === SimpleInfoAlertType.Info
      ? "border-light-blue-600"
      : type === SimpleInfoAlertType.Warning
        ? "border-yellow-600"
        : type === SimpleInfoAlertType.Critical
          ? "border-pink-600"
          : "border-transparent";

  return (
    <Tag
      {...extraAttributes}
      className={classNames(
        "flex flex-row",
        "rounded-xs border",
        // Compact: 24px tall in all (20px line + 2px padding + 2px border), the line
        // height of body text, so it can sit beside a name without growing the line.
        // The small icon is centred on the text.
        compact
          ? "items-center gap-xxs text-t-sm leading-[1.25rem]"
          : "items-start gap-xs text-t-md",
        padding,
        textColor,
        backgroundColor,
        borderColor,
        className
      )}
    >
      {/* System icon (uses Svg component for system icons) */}
      {compact ? (
        <span className="flex flex-none" aria-hidden="true">
          <Svg href={`/svg/system/${symbol}.svg`} size={SvgSize.SM} />
        </span>
      ) : (
        <Svg
          href={`/svg/system/${symbol}.svg`}
          size={SvgSize.LG}
          additionalClass="mr-sm"
        />
      )}

      <span>{content}</span>
    </Tag>
  );
}

export default SimpleInfoAlert;
